import React, { useState } from 'react';
import { X, Sparkles, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { INTEREST_OPTIONS } from '../../lib/constants';
import api from '../../lib/api';
import { toast } from 'sonner';

const DISMISS_KEY = 'hoibuur_interests_card_dismissed';

/**
 * InterestsOnboardingCard — vriendelijke, wegklikbare kaart bovenaan de feed
 * voor ingelogde gebruikers die nog geen interesses hebben ingesteld. Bij
 * opslaan schakelt de parent naar de "Voor jou"-tab.
 */
const InterestsOnboardingCard = ({ onSaved }) => {
  const { user, t, language, updateUser } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  if (!user || (user.interests && user.interests.length > 0) || dismissed) return null;

  const tr = (key, fallback) => (typeof t === 'function' ? (t(key) || fallback) : fallback);
  const options = INTEREST_OPTIONS.map(o => ({ ...o, label: o.label[language] || o.label.nl }));

  const toggle = (val) =>
    setSelected(prev => (prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]));

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      const res = await api.put('/users/profile', { interests: selected });
      updateUser(res.data);
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      toast.success(tr('interests_card.saved', 'Je interesses zijn opgeslagen!'));
      onSaved && onSaved();
    } catch (e) {
      toast.error(tr('interests_card.error', 'Kon interesses niet opslaan'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-6 rounded-2xl bg-gradient-to-br from-hoi-blue/5 to-hoi-coral/5 border border-hoi-blue/20 p-4 md:p-5 animate-fade-in-up"
      data-testid="interests-onboarding-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-hoi-blue/15 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-hoi-blue" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-lg text-primary leading-snug">
            {tr('interests_card.title', 'Wat vind jij leuk?')}
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {tr('interests_card.body', 'Kies je interesses en we tonen activiteiten die bij je passen onder "Voor jou".')}
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded-lg text-muted-foreground/70 hover:text-muted-foreground hover:bg-white/60 transition-colors"
          aria-label={tr('interests_card.dismiss', 'Sluiten')}
          data-testid="interests-onboarding-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {options.map(option => (
          <Badge
            key={option.value}
            variant={selected.includes(option.value) ? 'default' : 'outline'}
            className={`cursor-pointer px-3.5 py-2 rounded-full text-sm transition-all ${
              selected.includes(option.value)
                ? 'bg-hoi-coral text-white hover:bg-hoi-coral/90'
                : 'bg-white hover:bg-secondary'
            }`}
            onClick={() => toggle(option.value)}
            data-testid={`interests-chip-${option.value}`}
          >
            <span className="mr-1">{option.icon}</span>
            {option.label}
            {selected.includes(option.value) && <Check className="w-3 h-3 ml-1" />}
          </Badge>
        ))}
      </div>

      <div className="mt-4">
        <Button
          onClick={handleSave}
          disabled={selected.length === 0 || saving}
          className="rounded-full bg-hoi-blue hover:bg-hoi-blue/90 text-white font-heading px-6"
          data-testid="interests-onboarding-save"
        >
          {saving
            ? tr('interests_card.saving', 'Bezig...')
            : tr('interests_card.save', 'Opslaan & bekijk mijn feed')}
        </Button>
      </div>
    </div>
  );
};

export default InterestsOnboardingCard;
